import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, Users, Utensils, BedDouble, Sparkles, RefreshCw, LogOut } from "lucide-react";
import AdtreeLogo from "../components/AdtreeLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem("adtreego_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, fnb: 0, accommodation: 0, both: 0 });
  const [filter, setFilter] = useState("all");

  const verify = async (tk) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": tk } });
      return true;
    } catch {
      return false;
    }
  };

  const load = async (tk) => {
    setLoading(true);
    try {
      const [regsRes, statsRes] = await Promise.all([
        axios.get(`${API}/registrations`, { headers: { "X-Admin-Token": tk } }),
        axios.get(`${API}/registrations/stats`),
      ]);
      setItems(regsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (token) {
        const ok = await verify(token);
        if (ok) {
          setAuthed(true);
          load(token);
        } else {
          localStorage.removeItem("adtreego_admin_token");
          setToken("");
        }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onLogin = async (e) => {
    e.preventDefault();
    const ok = await verify(token);
    if (ok) {
      localStorage.setItem("adtreego_admin_token", token);
      setAuthed(true);
      load(token);
      toast.success("Login berhasil");
    } else {
      toast.error("Token tidak valid");
    }
  };

  const onLogout = () => {
    localStorage.removeItem("adtreego_admin_token");
    setToken("");
    setAuthed(false);
    setItems([]);
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <form onSubmit={onLogin} className="w-full max-w-md border border-brand-yellow/30 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-8">
            <AdtreeLogo size="md" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-brand-yellow">Admin</span>
          </div>
          <h1 className="font-anton uppercase text-3xl mb-2">Admin Dashboard</h1>
          <p className="text-white/60 text-sm mb-8">Masukkan admin token untuk melanjutkan.</p>
          <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
            Admin Token <span className="text-brand-yellow">*</span>
          </label>
          <input
            data-testid="admin-token-input"
            type="password"
            className="input-line mb-6"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="•••••••••••"
            required
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="w-full inline-flex items-center justify-center gap-3 bg-brand-yellow text-black font-anton uppercase tracking-widest text-sm px-6 py-3 hover:bg-white transition-colors"
          >
            <LogIn size={16} /> Login
          </button>
          <a href="/" className="block text-center mt-6 text-xs uppercase tracking-widest text-white/40 hover:text-brand-yellow">← back to site</a>
        </form>
      </div>
    );
  }

  const statCards = [
    { label: "Total Creators", value: stats.total, icon: Users },
    { label: "Dining", value: stats.fnb, icon: Utensils },
    { label: "Accommodation", value: stats.accommodation, icon: BedDouble },
    { label: "Both", value: stats.both, icon: Sparkles },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AdtreeLogo size="sm" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow">Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="refresh-btn"
            onClick={() => load(token)}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-white/20 text-white px-3 py-2 hover:bg-white/5"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-white/20 text-white px-3 py-2 hover:bg-white/5"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-screen-2xl mx-auto">
        <h1 className="font-anton uppercase text-4xl md:text-5xl mb-8">Creator Registrations</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mb-10">
          {statCards.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-black p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <Icon size={20} className="text-brand-yellow" />
                  <span className="text-[10px] uppercase tracking-widest text-white/40">0{i + 1}</span>
                </div>
                <div className="font-anton text-4xl md:text-5xl text-white">{s.value}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-2">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { k: "all", l: "All" },
            { k: "fnb", l: "Dining" },
            { k: "accommodation", l: "Accommodation" },
            { k: "both", l: "Both" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${
                filter === f.k
                  ? "bg-brand-yellow text-black border-brand-yellow"
                  : "border-white/20 text-white/70 hover:text-white"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">WhatsApp</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Instagram</th>
                <th className="text-left px-4 py-3">Followers</th>
                <th className="text-left px-4 py-3">City</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-white/40">
                    {loading ? "Loading..." : "No registrations yet."}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} data-testid={`reg-row-${r.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-medium text-white">{r.full_name}</td>
                    <td className="px-4 py-3 text-white/70">{r.email}</td>
                    <td className="px-4 py-3 text-white/70">{r.whatsapp}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-widest bg-brand-yellow/10 text-brand-yellow px-2 py-1">
                        {r.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/70">{r.instagram_handle || "-"}</td>
                    <td className="px-4 py-3 text-white/70">{r.followers_count ?? "-"}</td>
                    <td className="px-4 py-3 text-white/70">{r.city || "-"}</td>
                    <td className="px-4 py-3 text-white/50 text-xs">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
